import {
  CreateHoldTypeCommand,
  HoldReaction,
  HoldRestrictionType,
  HoldTypeRestrictionDto,
} from '@planning/app/api'
import { holdTypeService } from '@planning/services'
import { FieldStore, FormStore, ListFieldStore, required, validateSubForm } from '@tom-ui/utils'
import { action, makeObservable } from 'mobx'

export type HoldTypeRuleItem = {
  restrictionType: FieldStore<HoldRestrictionType>
  reaction: FieldStore<HoldReaction>
}

const createRuleItem = (value?: Partial<HoldTypeRestrictionDto>): HoldTypeRuleItem => ({
  restrictionType: new FieldStore<HoldRestrictionType>(
    (value?.restrictionType ?? '') as HoldRestrictionType,
  ),
  reaction: new FieldStore<HoldReaction>(((value?.reaction as HoldReaction) ?? '') as HoldReaction),
})

export interface HoldTypeFormData {
  type: string
  description: string
  userRoles: number[]
  restrictions: Array<HoldTypeRestrictionDto>
  isActive: boolean
}

export class HoldTypeFormStore extends FormStore<HoldTypeFormData> {
  type: FieldStore<string>
  description: FieldStore<string>
  userRoles: FieldStore<number[]>
  restrictions: ListFieldStore<HoldTypeRuleItem>
  isActive: FieldStore<boolean>

  existingHoldTypeNames: string[] = []
  originalName?: string

  constructor(initialValues?: Partial<HoldTypeFormData>) {
    super(initialValues)

    makeObservable(this, {
      submit: action,
      setData: action,
      reset: action,
      addRule: action,
      removeRule: action,
      setExistingHoldTypeNames: action,
      setOriginalName: action,
    })

    this.type = new FieldStore(initialValues?.type ?? '', required())
    this.description = new FieldStore(initialValues?.description ?? '')
    this.userRoles = new FieldStore<number[]>(initialValues?.userRoles ?? [])
    const existingRestrictions = initialValues?.restrictions ?? []
    this.restrictions = new ListFieldStore<HoldTypeRuleItem>(
      existingRestrictions.length > 0
        ? existingRestrictions.map(r => createRuleItem(r))
        : [createRuleItem()],
    )
    this.isActive = new FieldStore(initialValues?.isActive ?? true)
  }

  setExistingHoldTypeNames = (names: string[]) => {
    this.existingHoldTypeNames = names
  }

  setOriginalName = (name: string) => {
    this.originalName = name
  }

  submit = async (id?: number) => {
    const obj = this.data
    const cmd = this.mapToCommand(obj)

    try {
      if (id) {
        await holdTypeService.update({ ...cmd, id })
      } else {
        await holdTypeService.create(cmd)
      }
    } catch (e) {}
  }

  mapToCommand = (data: HoldTypeFormData): CreateHoldTypeCommand => {
    return {
      name: data.type,
      description: data.description,
      userRoles: data.userRoles,
      isActive: data.isActive,
      restrictions: data.restrictions,
    }
  }

  addRule = () => {
    this.restrictions.push(createRuleItem())
  }

  removeRule = (index: number) => {
    this.restrictions.removeByIndex(index)
  }

  setData(data: Partial<HoldTypeFormData>) {
    this.type.setValue(data.type ?? '')
    this.description.setValue(data.description ?? '')
    this.userRoles.setValue(data.userRoles ?? [])
    const restrictions = data.restrictions ?? []
    this.restrictions.setValue(
      restrictions.length > 0 ? restrictions.map(r => createRuleItem(r)) : [createRuleItem()],
    )
    this.isActive.setValue(data.isActive ?? true)
  }

  reset() {
    this.setData(this.initialValues)
  }

  validate = () => {
    let isValid = validateSubForm(this)

    const typeValue = this.type.value.trim().toLowerCase()
    const originalValue = this.originalName?.trim().toLowerCase()

    if (typeValue && typeValue !== originalValue) {
      if (this.existingHoldTypeNames.some(name => name.toLowerCase() === typeValue)) {
        this.type.setError('Hold type name already exists')
        isValid = false
      }
    }

    const restrictionPairs = new Set<string>()

    this.restrictions.value.forEach((r, index) => {
      let rowValid = true
      if (!r.restrictionType.value) {
        r.restrictionType.setError('Field is required')
        rowValid = false
      } else {
        r.restrictionType.setError(undefined)
      }

      if (!r.reaction.value) {
        r.reaction.setError('Field is required')
        rowValid = false
      } else {
        r.reaction.setError(undefined)
      }

      if (rowValid) {
        const pair = `${r.restrictionType.value}-${r.reaction.value}`
        if (restrictionPairs.has(pair)) {
          r.restrictionType.setError('Duplicated restriction and reaction')
          r.reaction.setError('Duplicated restriction and reaction')
          isValid = false
        } else {
          restrictionPairs.add(pair)
        }
      } else {
        isValid = false
      }
    })

    return isValid
  }

  get data(): HoldTypeFormData {
    return {
      type: this.type.value,
      description: this.description.value,
      userRoles: this.userRoles.value,
      restrictions: this.restrictions.value
        .filter(r => r.restrictionType.value !== ('' as any) && r.reaction.value !== ('' as any))
        .map(r => ({
          restrictionType: r.restrictionType.value,
          reaction: r.reaction.value,
        })),
      isActive: this.isActive.value,
    }
  }
}
